/**
 * Unified Data Loading Service
 *
 * This service handles all data loading scenarios in the application:
 * - Login flow data loading
 * - Page refresh data loading
 * - Account switch data loading
 *
 * Provides a consistent interface and loading experience across all scenarios.
 */

import { Meeting } from '../shared/types';
import { backgroundLoadingService } from './BackgroundLoadingService';
import { getDataSource } from './dataSource';

/**
 * Configuration for data loading operations
 */
export interface LoadingConfig {
  account: string;
  userId: string;
  triggerSource: 'login' | 'refresh' | 'account-switch';
  skipStages?: string[]; // For optimization (e.g., skip auth stage for account switch)
}

/**
 * Loading state for tracking progress
 */
export interface LoadingState {
  isLoading: boolean;
  loadingInitial: boolean;
  loadingMoreMeetings: boolean;
  error: string | null;
  meetings: Meeting[];
  hasInitiated: boolean;
  currentAccount: string | null;
}

/**
 * Callbacks for loading events
 */
export interface LoadingCallbacks {
  onLoadingStateChange: (state: Partial<LoadingState>) => void;
  onStageStart: (stageId: string) => void;
  onStageComplete: (stageId: string) => void;
  onError: (error: string) => void;
  onMeetingsUpdate: (meetings: Meeting[]) => void;
}


/**
 * Unified Data Loading Service Class
 */
export class DataLoadingService {
  private loadingState: LoadingState = {
    isLoading: false,
    loadingInitial: false,
    loadingMoreMeetings: false,
    error: null,
    meetings: [],
    hasInitiated: false,
    currentAccount: null
  };

  private callbacks?: LoadingCallbacks;
  private abortController?: AbortController;
  private readonly SESSION_KEY_PREFIX = 'dataLoadingService';
  private lastRefreshTime: number = 0;
  private refreshDebounceTimer?: NodeJS.Timeout;

  /**
   * Generate session storage key for account-specific data
   */
  private getSessionKey(account: string, key: string): string {
    return `${this.SESSION_KEY_PREFIX}_supabase_${account}_${key}`;
  }

  /**
   * Save meetings data to session storage
   */
  private saveMeetingsToSession(account: string, meetings: Meeting[]): void {
    try {
      const sessionData = {
        meetings,
        timestamp: Date.now(),
        account
      };
      sessionStorage.setItem(this.getSessionKey(account, 'meetings'), JSON.stringify(sessionData));
      // Saved ${meetings.length} meetings to session storage for ${account}
    } catch (error) {
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        console.warn(`Session storage quota exceeded for meetings. Performance caching disabled but functionality preserved.`);
        // Don't retry - meetings work fine without session persistence
      } else {
        console.warn('Failed to save meetings to session storage:', error);
      }
    }
  }

  /**
   * Load meetings data from session storage
   */
  private loadMeetingsFromSession(account: string): Meeting[] | null {
    try {
      const sessionData = sessionStorage.getItem(this.getSessionKey(account, 'meetings'));
      if (!sessionData) return null;

      const parsed = JSON.parse(sessionData);

      // Check if data is still valid (shorter TTL for data freshness)
      const age = Date.now() - parsed.timestamp;
      const maxAge = 2 * 60 * 1000; // 2 minutes for better data freshness

      if (age > maxAge) {
        // console.log(`Session meetings data expired for ${account} (${age/1000}s old), clearing...`);
        sessionStorage.removeItem(this.getSessionKey(account, 'meetings'));
        return null;
      }

      if (parsed.account === account && Array.isArray(parsed.meetings)) {
        // console.log(`Restored ${parsed.meetings.length} meetings from session storage for ${account}`);
        return parsed.meetings;
      }
    } catch (error) {
      console.warn('Failed to load meetings from session storage:', error);
      // Clear corrupted data
      sessionStorage.removeItem(this.getSessionKey(account, 'meetings'));
    }
    return null;
  }

  /**
   * Initialize the service with callbacks
   */
  initialize(callbacks: LoadingCallbacks) {
    this.callbacks = callbacks;
  }

  /**
   * Start data loading for the specified configuration
   */
  async loadData(config: LoadingConfig): Promise<void> {
    // Ensure service is initialized before proceeding
    if (!this.callbacks) {
      console.error('DataLoadingService not initialized - callbacks missing');
      throw new Error('DataLoadingService must be initialized with callbacks before use');
    }

    // Check for cached meetings first
    const cachedMeetings = this.loadMeetingsFromSession(config.account);
    if (cachedMeetings && cachedMeetings.length > 0) {
      // console.log(`Using cached meetings data for ${config.account} (${cachedMeetings.length} meetings) - skipping reload`);

      // Update state with cached data
      this.updateLoadingState({
        hasInitiated: true,
        currentAccount: config.account,
        meetings: cachedMeetings,
        isLoading: false,
        loadingInitial: false,
        error: null
      });

      // Validate cache consistency before using
      this.validateCacheConsistency(config.account, cachedMeetings);

      // CRITICAL: Update UI state with cached meetings data
      this.callbacks?.onMeetingsUpdate(cachedMeetings);

      // Complete meetings stage immediately
      this.callbacks?.onStageComplete('meetings');

      // Trigger background conversation loading if conversations aren't already cached
      this.triggerBackgroundConversationLoadingForCached(cachedMeetings as Meeting[]);

      return;
    }

    // Prevent multiple concurrent loading operations for the same account
    if (this.loadingState.hasInitiated && this.loadingState.currentAccount === config.account) {
      // console.log(`Data loading already initiated for ${config.account}, skipping duplicate - ${new Date().toISOString()}`);
      return;
    }

    // Abort any ongoing loading operation
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    // Reset state for new loading operation
    this.updateLoadingState({
      hasInitiated: true,
      currentAccount: config.account,
      isLoading: true,
      loadingInitial: true,
      error: null
      // Don't reset meetings array - preserve existing meetings during load
    });

    // console.log(`Starting data loading for account: ${config.account}, source: ${config.triggerSource} - ${new Date().toISOString()}`);

    try {
      await this.loadMeetingsData(config);
    } catch (error: unknown) {
      console.error(`Data loading failed for ${config.account}:`, error);
      this.handleLoadingError(error instanceof Error ? error.message : 'Error loading data');
    }
  }

  /**
   * Load meetings data with progressive loading and conversation preloading
   */
  private async loadMeetingsData(config: LoadingConfig): Promise<void> {
    // Check if operation was aborted
    if (this.abortController?.signal.aborted) {
      return;
    }

    // Start meetings stage
    this.callbacks?.onStageStart('meetings');

    try {
      // Fetch meetings progressively
      const { initialMeetings, allMeetingsPromise } = await getDataSource().fetchMeetingsProgressive(config.account);

      if (this.abortController?.signal.aborted) {
        return;
      }

      // Update meetings state
      this.updateLoadingState({
        meetings: initialMeetings as Meeting[]
      });
      this.callbacks?.onMeetingsUpdate(initialMeetings as Meeting[]);

      // Save meetings to session storage for persistence across navigation
      this.saveMeetingsToSession(config.account, initialMeetings as Meeting[]);

      // console.log(`Loaded ${initialMeetings.length} initial meetings for ${config.account} - ${new Date().toISOString()}`);

      // CRITICAL OPTIMIZATION: Complete meetings stage immediately for fast app reveal
      // console.log(`Meetings loaded: ${initialMeetings.length} initial meetings - completing stage immediately`);
      this.callbacks?.onStageComplete('meetings');

      // SMART PRIORITIZATION: Start ultra-high-priority conversation preloading immediately (first 3 meetings only)
      if (initialMeetings.length > 0) {
        // CRITICAL PATH: Execute immediately for fastest possible app reveal
        // console.log('Starting ULTRA-HIGH-PRIORITY conversation preloading immediately (critical path - first 3 meetings)');
        this.handleUltraHighPriorityConversationPreloading(initialMeetings as Meeting[])
          .catch(error => {
            console.warn('Ultra-high-priority conversation preloading failed (non-blocking):', error);
            // Don't throw - this is background loading and shouldn't block the main flow
          });
      } else {
        // CRITICAL FIX: When there are 0 meetings, immediately complete ultra-high-priority stage
        // This allows empty accounts to properly finish loading and close the loading screen
        // console.log('No meetings found - completing ultra-high-priority conversation stage immediately');
        this.callbacks?.onStageComplete('ultra-high-priority-conversations');

        // Also start parallel loading to handle background stages (even with 0 meetings)
        this.startParallelHighPriorityAndRemainingLoading([]);
      }

      // Handle progressive meetings loading in background
      if (allMeetingsPromise && !this.abortController?.signal.aborted) {
        this.handleProgressiveMeetings(allMeetingsPromise, config.account);
      }

      // Complete initial loading
      this.updateLoadingState({
        loadingInitial: false,
        isLoading: false
      });

      console.log(`Data loading completed for ${config.account} - ${new Date().toISOString()}`);

    } catch (error: unknown) {
      if (!this.abortController?.signal.aborted) {
        throw error;
      }
    }
  }

  /**
   * Handle ULTRA-HIGH-PRIORITY conversation preloading (first 3 meetings only - critical path)
   */
  private async handleUltraHighPriorityConversationPreloading(meetings: Meeting[]): Promise<void> {
    if (meetings.length > 0) {
      // Verbose debug log - commented for production
      // console.log(`Starting ULTRA-HIGH-PRIORITY conversation preloading for first 3 meetings... - ${new Date().toISOString()}`);

      // Mark stage as started
      this.callbacks?.onStageStart('ultra-high-priority-conversations');

      try {
        await getDataSource().preloadConversations(meetings, 'ultra-high');
        // Verbose debug log - commented for production
        // console.log(`ULTRA-HIGH-PRIORITY conversation preloading completed - ${new Date().toISOString()}`);

        // CRITICAL: Mark the ultra-high-priority-conversations stage as complete (APP CAN NOW REVEAL)
        this.callbacks?.onStageComplete('ultra-high-priority-conversations');

        // After ultra-high-priority conversations are cached, start PARALLEL loading of:
        // 1. High-priority conversations (remaining 7 from top 10)
        // 2. Remaining conversations (all other meetings)
        this.startParallelHighPriorityAndRemainingLoading(meetings);

      } catch (error) {
        console.warn('Ultra-high-priority conversation preloading failed:', error);
        // Even on failure, we should complete the stage to avoid blocking the UI
        this.callbacks?.onStageComplete('ultra-high-priority-conversations');
        // Don't throw - preloading failure shouldn't break the loading flow
      }
    } else {
      // No meetings to preload, complete the stage immediately
      this.callbacks?.onStageComplete('ultra-high-priority-conversations');
    }
  }

  /**
   * Handle HIGH-PRIORITY conversation preloading (meetings 4-10 from top 10 - background)
   */
  private async handleHighPriorityConversationPreloading(meetings: Meeting[]): Promise<void> {
    if (meetings.length > 3) {
      // Verbose debug log - commented for production
      // console.log(`Starting HIGH-PRIORITY background conversation preloading for meetings 4-10... - ${new Date().toISOString()}`);

      // Mark stage as started
      this.callbacks?.onStageStart('high-priority-conversations');

      try {
        await getDataSource().preloadConversations(meetings, 'high');
        // Verbose debug log - commented for production
        // console.log(`HIGH-PRIORITY background conversation preloading completed - ${new Date().toISOString()}`);

        // Mark the high-priority-conversations stage as complete
        this.callbacks?.onStageComplete('high-priority-conversations');

      } catch (error) {
        console.warn('High-priority background conversation preloading failed:', error);
        // Even on failure, complete the stage to avoid blocking background loading
        this.callbacks?.onStageComplete('high-priority-conversations');
        // Don't throw - preloading failure shouldn't break the loading flow
      }
    } else {
      // Less than 4 meetings total, no high-priority background loading needed
      this.callbacks?.onStageComplete('high-priority-conversations');
    }
  }

  /**
   * Start parallel loading of high-priority + remaining conversations + inbox data
   */
  private startParallelHighPriorityAndRemainingLoading(meetings: Meeting[]): void {
    // Verbose debug log - commented for production
    // console.log(`Starting PARALLEL loading: high-priority (7) + remaining conversations + inbox data - ${new Date().toISOString()}`);

    // Queue high-priority conversation preloading (meetings 4-10) with highest priority
    if (meetings.length > 3) {
      // Verbose debug log - commented for production
      // console.log(`Queueing HIGH-PRIORITY conversation preloading for meetings 4-10`);
      backgroundLoadingService.addToQueue(
        'high-priority-conversations',
        30, // HIGHEST: Priority 30 for remaining 7 high-priority conversations
        async () => {
          await this.handleHighPriorityConversationPreloading(meetings);
        }
      );
    } else {
      // CRITICAL FIX: Complete high-priority-conversations immediately when < 4 meetings
      console.log(`Insufficient meetings (${meetings.length}) for high-priority preloading - completing stage immediately`);
      this.callbacks?.onStageComplete('high-priority-conversations');
    }

    // Queue remaining conversation preloading with medium priority
    if (meetings.length > 10) {
      // Verbose debug log - commented for production
      // console.log(`Queueing REMAINING conversation preloading for ${meetings.length - 10} meetings`);
      backgroundLoadingService.addToQueue(
        'remaining-conversations',
        20, // OPTIMIZED: Increased priority from 15 to 20 for faster loading
        async () => {
          await this.handleRemainingConversationPreloading(meetings);
        }
      );
    } else {
      // CRITICAL FIX: Complete remaining-conversations immediately when <= 10 meetings
      console.log(`Insufficient meetings (${meetings.length}) for remaining preloading - completing stage immediately`);
      this.callbacks?.onStageComplete('remaining-conversations');
    }
  }

  /**
   * Handle REMAINING conversation preloading (meetings beyond first 10)
   */
  private async handleRemainingConversationPreloading(meetings: Meeting[]): Promise<void> {
    if (meetings.length > 10) {
      // Verbose debug log - commented for production
      // console.log(`Starting REMAINING conversation preloading for ${meetings.length - 10} meetings... - ${new Date().toISOString()}`);

      // Mark stage as started
      this.callbacks?.onStageStart('remaining-conversations');

      try {
        await getDataSource().preloadConversations(meetings, 'remaining');
        // Verbose debug log - commented for production
        // console.log(`REMAINING conversation preloading completed - ${new Date().toISOString()}`);

        // Mark the remaining-conversations stage as complete
        this.callbacks?.onStageComplete('remaining-conversations');
      } catch (error) {
        console.warn('Remaining conversation preloading failed:', error);
        // Even on failure, complete the stage to avoid blocking background loading
        this.callbacks?.onStageComplete('remaining-conversations');
        // Don't throw - preloading failure shouldn't break the loading flow
      }
    } else {
      // No remaining meetings to preload, complete the stage immediately
      this.callbacks?.onStageComplete('remaining-conversations');
    }
  }

  /**
   * Trigger background conversation loading for cached meetings (check if already cached)
   */
  private triggerBackgroundConversationLoadingForCached(meetings: Meeting[]): void {
    // For cached meetings, we still want to ensure conversations are available
    // Since conversation cache is now persistent, conversations might already be cached
    // Verbose debug log - commented for production
    // console.log(`Using cached meetings with persistent conversation cache - conversations likely already available`);

    // CRITICAL: For cached meetings, we must still complete the ultra-high-priority stage
    // This ensures the loading screen closes properly even with cached data
    // Verbose debug log - commented for production
    // console.log(`Starting ULTRA-HIGH-PRIORITY conversation check for cached meetings (immediate execution)`);
    this.handleUltraHighPriorityConversationPreloading(meetings)
      .catch(error => {
        console.warn('Ultra-high-priority conversation check failed for cached meetings (non-blocking):', error);
        // Don't throw - this is background loading
      });
  }

  /**
   * Handle progressive meetings loading in background
   */
  private handleProgressiveMeetings(allMeetingsPromise: Promise<Meeting[]>, account: string): void {
    this.updateLoadingState({ loadingMoreMeetings: true });

    allMeetingsPromise
      .then(allSubsequentMeetings => {
        if (this.abortController?.signal.aborted || this.loadingState.currentAccount !== account) {
          return;
        }

        // Merge new meetings with existing ones
        const existingMeetingIds = new Set(this.loadingState.meetings.map(m => m.id));
        const newSubsequentMeetings = (allSubsequentMeetings as Meeting[]).filter(m => !existingMeetingIds.has(m.id));
        const combinedMeetings = [...this.loadingState.meetings, ...newSubsequentMeetings];

        // Sort by meeting date (newest first)
        combinedMeetings.sort((a, b) =>
          new Date(b.fields['Fecha Reunión']).getTime() - new Date(a.fields['Fecha Reunión']).getTime()
        );

        this.updateLoadingState({ meetings: combinedMeetings });
        this.callbacks?.onMeetingsUpdate(combinedMeetings);

        // Update session storage with complete meetings list
        this.saveMeetingsToSession(account, combinedMeetings);

        // Verbose debug log - commented for production
        // console.log(`Progressive loading completed: ${combinedMeetings.length} total meetings - ${new Date().toISOString()}`);

        // Note: Conversations for progressive meetings are loaded on-demand only
        // This keeps the system scalable for accounts with 100s of meetings
      })
      .catch(error => {
        if (!this.abortController?.signal.aborted) {
          console.error('Error fetching all subsequent meetings:', error);
          // Don't update error state - this is background loading
        }
      })
      .finally(() => {
        if (!this.abortController?.signal.aborted && this.loadingState.currentAccount === account) {
          this.updateLoadingState({ loadingMoreMeetings: false });
        }
      });
  }

  /**
   * Handle loading errors
   */
  private handleLoadingError(errorMessage: string): void {
    this.updateLoadingState({
      error: errorMessage,
      loadingInitial: false,
      loadingMoreMeetings: false,
      isLoading: false
    });

    this.callbacks?.onError(errorMessage);
    this.callbacks?.onStageComplete('meetings'); // Complete stage even on error
  }

  /**
   * Update loading state and notify callbacks
   */
  private updateLoadingState(updates: Partial<LoadingState>): void {
    // DEBUG: Track all state updates
    // if (updates.loadingInitial !== undefined || updates.meetings !== undefined) {
    //   console.log(`DataLoadingService.updateLoadingState called:`, {
    //     loadingInitial: updates.loadingInitial,
    //     meetingsLength: updates.meetings?.length,
    //     currentAccount: this.loadingState.currentAccount,
    //     timestamp: new Date().toISOString(),
    //     stack: new Error().stack?.split('\n')[2]?.trim()
    //   });
    // }

    this.loadingState = { ...this.loadingState, ...updates };
    this.callbacks?.onLoadingStateChange(updates);
  }

  /**
   * Clear session storage for an account
   */
  clearSessionStorage(account: string): void {
    try {
      sessionStorage.removeItem(this.getSessionKey(account, 'meetings'));
      console.log(`Cleared session storage for ${account}`);
    } catch (error) {
      console.warn('Failed to clear session storage:', error);
    }
  }

  /**
   * Validate cache consistency and fix any issues
   */
  private validateCacheConsistency(account: string, meetings: Meeting[]): boolean {
    try {
      // Check for duplicate meetings by ID
      const seenIds = new Set<string>();
      const duplicates: string[] = [];

      for (const meeting of meetings) {
        if (seenIds.has(meeting.id)) {
          duplicates.push(meeting.id);
        }
        seenIds.add(meeting.id);
      }

      if (duplicates.length > 0) {
        console.warn(`Found duplicate meetings in cache for ${account}:`, duplicates);

        // Remove duplicates, keeping the first occurrence
        const uniqueMeetings = meetings.filter((meeting, index, array) =>
          array.findIndex(m => m.id === meeting.id) === index
        );

        // Update cache with cleaned data
        this.saveMeetingsToSession(account, uniqueMeetings);
        this.loadingState.meetings = uniqueMeetings;
        this.callbacks?.onMeetingsUpdate(uniqueMeetings);

        console.log(`Fixed cache consistency - removed ${duplicates.length} duplicates`);
        return false; // Indicates cache was fixed
      }

      return true; // Cache is consistent
    } catch (error) {
      console.warn('Failed to validate cache consistency:', error);
      return true; // Assume cache is fine if validation fails
    }
  }

  /**
   * Reset loading state (for account switches or logout)
   */
  resetState(): void {
    if (this.abortController) {
      this.abortController.abort();
    }

    // Clear session storage for current account
    if (this.loadingState.currentAccount) {
      this.clearSessionStorage(this.loadingState.currentAccount);
    }

    // Clear conversation cache as well
    getDataSource().clearConversationCache();

    this.loadingState = {
      isLoading: false,
      loadingInitial: false,
      loadingMoreMeetings: false,
      error: null,
      meetings: [],
      hasInitiated: false,
      currentAccount: null
    };

    this.updateLoadingState(this.loadingState);
  }

  /**
   * Get current loading state
   */
  getLoadingState(): LoadingState {
    return { ...this.loadingState };
  }

  /**
   * Check if currently loading for a specific account
   */
  isLoadingForAccount(account: string): boolean {
    return this.loadingState.isLoading && this.loadingState.currentAccount === account;
  }

  /**
   * Invalidate meetings cache for a specific account
   * This forces a fresh load on next access
   */
  invalidateMeetingsCache(account: string): void {
    try {
      this.clearSessionStorage(account);
      console.log(`Invalidated meetings cache for ${account} - will reload fresh data`);

      // If this is the current account, reset hasInitiated to allow fresh load
      if (this.loadingState.currentAccount === account) {
        this.loadingState.hasInitiated = false;
      }
    } catch (error) {
      console.warn('Failed to invalidate meetings cache:', error);
    }
  }

  /**
   * Refresh meetings data for a specific account
   * Options:
   * - skipCache: Force fresh load even if cache exists
   * - silent: Don't show loading states (for background refresh)
   * - debounce: Prevent rapid successive refreshes (default: true)
   */
  async refreshMeetings(
    account: string,
    userId: string,
    options?: { skipCache?: boolean; silent?: boolean; debounce?: boolean }
  ): Promise<void> {
    // Debounce rapid refresh requests by default
    const shouldDebounce = options?.debounce !== false;

    if (shouldDebounce) {
      // Clear any pending refresh
      if (this.refreshDebounceTimer) {
        clearTimeout(this.refreshDebounceTimer);
      }

      // Check if refresh was called too recently (within 2 seconds)
      const now = Date.now();
      const timeSinceLastRefresh = now - this.lastRefreshTime;

      if (timeSinceLastRefresh < 2000) {
        console.log(`Debouncing refresh request for ${account} (last refresh ${timeSinceLastRefresh}ms ago)`);

        // Schedule a delayed refresh
        return new Promise((resolve, reject) => {
          this.refreshDebounceTimer = setTimeout(async () => {
            try {
              await this.refreshMeetings(account, userId, { ...options, debounce: false });
              resolve();
            } catch (error) {
              reject(error);
            }
          }, 2000 - timeSinceLastRefresh);
        });
      }
    }

    console.log(`Refreshing meetings for ${account} - options:`, options);
    this.lastRefreshTime = Date.now();

    // If skipCache is true, clear the cache first
    if (options?.skipCache) {
      this.invalidateMeetingsCache(account);
    }

    // Store current loading states if silent mode
    const previousStates = options?.silent ? {
      loadingInitial: this.loadingState.loadingInitial,
      isLoading: this.loadingState.isLoading
    } : null;

    try {
      // Reset hasInitiated to allow fresh load
      if (this.loadingState.currentAccount === account) {
        this.loadingState.hasInitiated = false;
      }

      // Load fresh data
      await this.loadData({
        account,
        userId,
        triggerSource: 'refresh'
      });

      console.log(`Meetings refresh completed for ${account}`);
    } catch (error) {
      console.error(`Failed to refresh meetings for ${account}:`, error);
      throw error;
    } finally {
      // Restore loading states if silent mode
      if (options?.silent && previousStates) {
        this.updateLoadingState(previousStates);
      }
    }
  }
}

// Export singleton instance
export const dataLoadingService = new DataLoadingService();

export default dataLoadingService;